import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { useLanguage } from "../../i18n";
import { api, ApiError, resolveUrl, type RentalAgreementStatus } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { loadRazorpayCheckout } from "../../razorpayCheckout";

// window.open() (even the synchronous-before-await trick below) is a
// browser-only mechanism — Capacitor's Android/iOS WebView has no tab
// concept and silently no-ops it. The native shell needs the Browser
// plugin's in-app browser instead (see downloadUtils.ts for the same
// native/web split applied to downloads).
const isNative = Capacitor.isNativePlatform();

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

// Mirrors OWNER_ESTAMP_FEE in server/src/plans.ts — used only as the
// display fallback before an agreement row (and its real feeAmount) exists;
// the server is always the source of truth for what's actually charged.
const ESTAMP_FEE = 299;

// Flip to true once the eSahayak wallet is funded — see the conversation
// that paused this (no real stamp duty balance yet). While false, the
// stamp-request and identity-verification flow below is replaced with a
// "coming soon" card, an interest signal, and a manual document upload so
// owners aren't fully blocked in the meantime.
const ESTAMP_LIVE = false;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export interface AgreementParty {
  tenantName: string;
  tenantPhone?: string | null;
  ownerName?: string | null;
  ownerPhone?: string | null;
  propertyLabel: string;
  propertyLocality?: string | null;
  propertyType?: string | null;
  rent: number;
  deposit: number;
  startDate: string;
}

const IN_PROGRESS_STAMP_STATUSES = new Set(["placed", "assigned", "generating", "verifying"]);

export function RentalAgreement({
  onBack,
  tenancyId,
  ...p
}: AgreementParty & { onBack: () => void; tenancyId: string }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const toast = useToast();

  const [status, setStatus] = useState<RentalAgreementStatus | null | undefined>(undefined);
  const [sending, setSending] = useState(false);
  // True once Razorpay's own handler confirms the fee payment succeeded,
  // until our webhook catches up (see TenantOnboarding.tsx's identical
  // depositConfirming pattern for why this must never fall back to showing
  // the pay button again — money already moved).
  const [sendingConfirming, setSendingConfirming] = useState(false);
  const [openingCertificate, setOpeningCertificate] = useState(false);
  const [ownerVerifiedName, setOwnerVerifiedName] = useState<string | null>(null);
  const [tenantVerifiedName, setTenantVerifiedName] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [registeringInterest, setRegisteringInterest] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [removingDoc, setRemovingDoc] = useState(false);

  function loadStatus() {
    api
      .get(`/api/tenancies/${tenancyId}/agreement`)
      .then((d) => {
        setStatus(d.agreement);
        setOwnerVerifiedName(d.ownerDigilockerVerifiedName ?? null);
        setTenantVerifiedName(d.tenantDigilockerVerifiedName ?? null);
      })
      .catch(() => setStatus(null));
  }
  useEffect(loadStatus, [tenancyId]);

  async function registerInterest() {
    setRegisteringInterest(true);
    try {
      const { agreement } = await api.post(`/api/tenancies/${tenancyId}/agreement/interest`, {});
      setStatus(agreement);
      toast(t("rentalAgreement.interestToast"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setRegisteringInterest(false);
    }
  }

  async function handleUploadFile(file: File) {
    setUploadingDoc(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { agreement } = await api.post(`/api/tenancies/${tenancyId}/agreement/upload-document`, { dataUrl, filename: file.name });
      setStatus(agreement);
      toast(t("rentalAgreement.uploadDocSuccessToast"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("rentalAgreement.uploadDocFailedToast"));
    } finally {
      setUploadingDoc(false);
    }
  }

  async function removeUploadedDoc() {
    if (!confirm(t("rentalAgreement.removeDocConfirm"))) return;
    setRemovingDoc(true);
    try {
      const { agreement } = await api.del(`/api/tenancies/${tenancyId}/agreement/upload-document`);
      setStatus(agreement);
      toast(t("rentalAgreement.removeDocSuccessToast"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setRemovingDoc(false);
    }
  }

  async function viewUploadedDoc() {
    if (!status?.uploadedDocumentUrl) return;
    const url = resolveUrl(status.uploadedDocumentUrl);
    if (isNative) await Browser.open({ url });
    else window.open(url, "_blank");
  }

  async function verifyWithDigilocker() {
    setVerifying(true);
    // Same synchronous-window.open-before-await pattern as viewStampedDocument
    // (web only — native opens via the Browser plugin instead, see below).
    const tab = isNative ? null : window.open("", "_blank");
    try {
      const body = user?.role === "tenant" ? { tenancyId } : {};
      const { url } = await api.post("/api/digilocker/start", body);
      if (!url) {
        tab?.close();
        return;
      }
      if (isNative) await Browser.open({ url });
      else if (tab) tab.location.href = url;
    } catch (err) {
      tab?.close();
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setVerifying(false);
    }
  }

  async function sendForStamp() {
    setSending(true);
    try {
      // The fee already cleared on an earlier attempt (order placement
      // itself failed that time) — retry placing it without charging again.
      if (status?.feeStatus === "paid") {
        await api.post(`/api/tenancies/${tenancyId}/agreement/request`, {});
        toast(t("rentalAgreement.stampSentToast"));
        loadStatus();
        return;
      }

      const order = await api.post(`/api/tenancies/${tenancyId}/agreement/fee-checkout`, {});
      // Premium's 1 free e-stamp/month — the server already placed the
      // eSahayak order directly, no payment to collect.
      if (order.freeEstampUsed) {
        setStatus(order.agreement);
        toast(t("rentalAgreement.freeEstampToast"));
        return;
      }

      await loadRazorpayCheckout();
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency,
          order_id: order.orderId,
          name: "ToLetBook",
          image: "https://toletbook.in/icons/icon-512.png",
          description: t("rentalAgreement.stampFeePaymentDesc"),
          handler: () => resolve(),
          modal: { ondismiss: () => reject(new Error("cancelled")) },
        });
        rzp.on("payment.failed", () => reject(new Error("failed")));
        rzp.open();
      });
      // Razorpay's own handler only fires on a real successful charge — the
      // fee is paid from here on; we're just waiting for our webhook to
      // place the actual eSahayak order. Poll for up to ~40s.
      setSendingConfirming(true);
      for (let i = 0; i < 20; i++) {
        const fresh = await api.get(`/api/tenancies/${tenancyId}/agreement`);
        if (fresh.agreement && fresh.agreement.stampStatus !== "not_started") {
          setStatus(fresh.agreement);
          setSendingConfirming(false);
          toast(t("rentalAgreement.stampSentToast"));
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      setSendingConfirming(false);
      loadStatus();
    } catch (err) {
      if (err instanceof Error && err.message === "failed") toast(t("rentalAgreement.stampFeeFailedToast"));
      else if (!(err instanceof Error && err.message === "cancelled")) {
        toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
      }
    } finally {
      setSending(false);
    }
  }

  async function viewStampedDocument() {
    setOpeningCertificate(true);
    // Opened synchronously, before the await below, so browsers still treat
    // it as a direct result of the click — a window.open() call made after
    // an await loses that "user gesture" trust and gets silently popup-blocked.
    // (Native skips this entirely — see the isNative comment above.)
    const tab = isNative ? null : window.open("", "_blank");
    try {
      const { documentUrl } = await api.get(`/api/tenancies/${tenancyId}/agreement/certificate`);
      if (!documentUrl) {
        tab?.close();
        return;
      }
      if (isNative) await Browser.open({ url: documentUrl });
      else if (tab) tab.location.href = documentUrl;
    } catch (err) {
      tab?.close();
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setOpeningCertificate(false);
    }
  }

  const stampStatus = status?.stampStatus;
  const isOwner = user?.role === "owner";

  return (
    <>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onBack}>
        {t("rentalAgreement.back")}
      </button>
      <div className="reg-title">{t("rentalAgreement.title")}</div>
      <div className="reg-sub">{p.propertyLabel}</div>

      {!ESTAMP_LIVE && status !== undefined && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="sub" style={{ marginBottom: 4, fontWeight: 700 }}>{t("rentalAgreement.comingSoonTitle")}</div>
          <div className="sub" style={{ fontSize: 12.5 }}>{t("rentalAgreement.comingSoonBody")}</div>
          {isOwner && (
            <button
              className={status?.interestRegisteredAt ? "btn-outline" : "btn-solid"}
              style={{ marginTop: 10 }}
              disabled={registeringInterest || !!status?.interestRegisteredAt}
              onClick={registerInterest}
            >
              {status?.interestRegisteredAt
                ? t("rentalAgreement.interestRegisteredBtn")
                : registeringInterest
                  ? t("common.saving")
                  : t("rentalAgreement.interestBtn")}
            </button>
          )}
        </div>
      )}

      {!ESTAMP_LIVE && status !== undefined && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="sub" style={{ marginBottom: 4, fontWeight: 700 }}>{t("rentalAgreement.uploadDocHeading")}</div>
          {isOwner && <div className="sub" style={{ fontSize: 12.5, marginBottom: 10 }}>{t("rentalAgreement.uploadDocHint")}</div>}

          {status?.uploadedDocumentUrl ? (
            <>
              <div className="card-row">
                <span className="sub">{t("rentalAgreement.uploadedOnLabel", { date: fmtDate(status.uploadedAt) })}</span>
              </div>
              <button className="btn-solid" style={{ marginTop: 10 }} onClick={viewUploadedDoc}>
                {t("rentalAgreement.viewUploadedDocBtn")}
              </button>
              {isOwner && (
                <button className="btn-outline btn-sm" style={{ marginTop: 8 }} disabled={removingDoc} onClick={removeUploadedDoc}>
                  {removingDoc ? t("common.saving") : t("rentalAgreement.removeUploadedDocBtn")}
                </button>
              )}
            </>
          ) : isOwner ? (
            <label className="btn-outline" style={{ display: "block", textAlign: "center", cursor: uploadingDoc ? "default" : "pointer", opacity: uploadingDoc ? 0.6 : 1 }}>
              {uploadingDoc ? t("common.saving") : t("rentalAgreement.uploadDocBtn")}
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                disabled={uploadingDoc}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) handleUploadFile(file);
                }}
              />
            </label>
          ) : (
            <div className="sub" style={{ fontSize: 12.5 }}>{t("rentalAgreement.noUploadedDocTenantHint")}</div>
          )}
        </div>
      )}

      {ESTAMP_LIVE && status !== undefined && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="sub" style={{ marginBottom: 8, fontWeight: 700 }}>{t("rentalAgreement.identityVerificationLabel")}</div>
          <div className="card-row">
            <span className="sub">{t("rentalAgreement.ownerLabel")}</span>
            {ownerVerifiedName ? (
              <span className="badge paid">{t("rentalAgreement.digilockerVerifiedBadge")}</span>
            ) : (
              <span className="badge vacant">{t("rentalAgreement.digilockerNotVerifiedBadge")}</span>
            )}
          </div>
          {ownerVerifiedName && (
            <div className="sub" style={{ marginTop: 2, fontSize: 13 }}>
              {ownerVerifiedName}
            </div>
          )}
          <div className="card-row" style={{ marginTop: 8 }}>
            <span className="sub">{t("rentalAgreement.tenantLabel")}</span>
            {tenantVerifiedName ? (
              <span className="badge paid">{t("rentalAgreement.digilockerVerifiedBadge")}</span>
            ) : (
              <span className="badge vacant">{t("rentalAgreement.digilockerNotVerifiedBadge")}</span>
            )}
          </div>
          {tenantVerifiedName && (
            <div className="sub" style={{ marginTop: 2, fontSize: 13 }}>
              {tenantVerifiedName}
            </div>
          )}
          {((isOwner && !ownerVerifiedName) || (!isOwner && !tenantVerifiedName)) && (
            <button className="btn-outline" style={{ marginTop: 10 }} disabled={verifying} onClick={verifyWithDigilocker}>
              {verifying ? t("rentalAgreement.verifyingDigilockerBtn") : t("rentalAgreement.verifyDigilockerBtn")}
            </button>
          )}
        </div>
      )}

      {ESTAMP_LIVE && status !== undefined && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="card-row">
            <span className="sub">{t("rentalAgreement.stampStatusLabel")}</span>
            {!stampStatus || stampStatus === "not_started" || stampStatus === "cancelled" ? (
              <span className="badge vacant">{t("rentalAgreement.stampStatusNotStarted")}</span>
            ) : stampStatus === "verified" ? (
              <span className="badge paid">{t("rentalAgreement.stampStatusVerified")}</span>
            ) : (
              <span className="badge pending">{t("rentalAgreement.stampStatusProcessing")}</span>
            )}
          </div>

          {isOwner && (!stampStatus || stampStatus === "not_started" || stampStatus === "cancelled") && (
            <>
              <div className="sub" style={{ marginTop: 8 }}>
                {status?.feeStatus === "paid"
                  ? t("rentalAgreement.stampFeeAlreadyPaidHint")
                  : t("rentalAgreement.stampHint", { amount: fmtINR(status?.feeAmount ?? ESTAMP_FEE) })}
              </div>
              <button className="btn-solid" style={{ marginTop: 10 }} disabled={sending || sendingConfirming} onClick={sendForStamp}>
                {sendingConfirming
                  ? t("rentalAgreement.confirmingPaymentBtn")
                  : sending
                    ? t("rentalAgreement.sendingStampBtn")
                    : status?.feeStatus === "paid"
                      ? t("rentalAgreement.retrySendForStampBtn")
                      : t("rentalAgreement.sendForStampBtn", { amount: fmtINR(status?.feeAmount ?? ESTAMP_FEE) })}
              </button>
            </>
          )}

          {stampStatus && IN_PROGRESS_STAMP_STATUSES.has(stampStatus) && (
            <button className="btn-outline btn-sm" style={{ marginTop: 10 }} onClick={loadStatus}>
              {t("rentalAgreement.refreshStatusBtn")}
            </button>
          )}

          {stampStatus === "verified" && (
            <button className="btn-solid" style={{ marginTop: 10 }} disabled={openingCertificate} onClick={viewStampedDocument}>
              {t("rentalAgreement.viewStampedBtn")}
            </button>
          )}
        </div>
      )}

      <div className="card" style={{ marginTop: 4 }}>
        <div className="card-row">
          <span className="sub">{t("rentalAgreement.ownerLabel")}</span>
          <b>{ownerVerifiedName || p.ownerName || "—"}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentalAgreement.tenantLabel")}</span>
          <b>{tenantVerifiedName || p.tenantName}</b>
        </div>
      </div>

      <div className="card">
        <div className="card-row">
          <span className="sub">{t("rentalAgreement.propertyLabel")}</span>
          <b>
            {p.propertyLabel}
            {p.propertyLocality ? `, ${p.propertyLocality}` : ""}
          </b>
        </div>
        {p.propertyType && (
          <div className="card-row" style={{ marginTop: 8 }}>
            <span className="sub">{t("rentalAgreement.typeLabel")}</span>
            <b>{p.propertyType}</b>
          </div>
        )}
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentalAgreement.monthlyRentLabel")}</span>
          <b>{fmtINR(p.rent)}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentalAgreement.securityDepositLabel")}</span>
          <b>{fmtINR(p.deposit)}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentalAgreement.moveInDateLabel")}</span>
          <b>{fmtDate(p.startDate)}</b>
        </div>
      </div>
    </>
  );
}
